/* eslint-disable */
import { useCallback, useMemo } from 'react';
import { apiCaller } from 'utils/axios';
import { API_ENDPOINTS } from '../apiEndpoints';
import Swal from 'sweetalert2';
import { useAcquittalsContext } from './context';
import { ACTIONS } from './reducer';

export const useAcquittals = () => {
    const [state, dispatch] = useAcquittalsContext();

    const setLoading = useCallback(
        (loading) => {
            dispatch({ type: ACTIONS.SET_LOADING, payload: loading });
        },
        [dispatch]
    );

    const showSuccessMessage = useCallback((message) => {
        Swal.fire({
            title: 'Success',
            text: message,
            icon: 'success'
        });
    }, []);

    const showErrorMessage = useCallback(
        (error) => {
            Swal.fire({
                title: 'Error',
                text: error.message || 'An unexpected error occurred',
                icon: 'error'
            });
            dispatch({ type: ACTIONS.SET_ERROR, payload: error });
        },
        [dispatch]
    );

    const fetchPendingAcquittals = useCallback(
        async (currentApprovalStage, status) => {
            try {
                setLoading(true);
                const queryParams = new URLSearchParams();
                if (currentApprovalStage) queryParams.append('currentApprovalStage', currentApprovalStage);
                if (status) queryParams.append('status', status);

                const endpoint = `${API_ENDPOINTS.TRAVEL_ACQUITALS}/approvals${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
                const data = await apiCaller.get(endpoint);
                dispatch({ type: ACTIONS.SET_PENDING_ACQUITTALS, payload: data });
                return data;
            } catch (error) {
                showErrorMessage(error);
                throw error;
            } finally {
                setLoading(false);
            }
        },
        [setLoading, showErrorMessage, dispatch]
    );

    const approveAcquittals = useCallback(
        async (acquittalIds, currentApproverLevel, status, comments) => {
            try {
                setLoading(true);
                const data = await apiCaller.post(`${API_ENDPOINTS.TRAVEL_ACQUITALS}/approvals`, {
                    acquittalIds,
                    currentApproverLevel,
                    status,
                    comments
                });
                return data;
            } catch (error) {
                showErrorMessage(error);
                throw error;
            } finally {
                setLoading(false);
            }
        },
        [setLoading, showErrorMessage]
    );

    const getAcquittalStatuses = useCallback(
        async (id) => {
            try {
                setLoading(true);
                const data = await apiCaller.get(`${API_ENDPOINTS.TRAVEL_ACQUITALS}/${id}/statuses`);
                return data;
            } catch (error) {
                showErrorMessage(error);
                throw error;
            } finally {
                setLoading(false);
            }
        },
        [setLoading, showErrorMessage]
    );

    const getTimelineStatuses = useCallback(
        async (id) => {
            try {
                setLoading(true);
                const data = await apiCaller.get(`${API_ENDPOINTS.TRAVEL_ACQUITALS}/${id}/statuses`);
                dispatch({ type: ACTIONS.SET_ACQUITTAL_TIMELINE, payload: data });
                return data;
            } catch (error) {
                showErrorMessage(error);
                throw error;
            } finally {
                setLoading(false);
            }
        },
        [setLoading, showErrorMessage, dispatch]
    );

    const getAcquittalById = useCallback(
        async (id) => {
            try {
                setLoading(true);
                const data = await apiCaller.get(`${API_ENDPOINTS.TRAVEL_ACQUITALS}/${id}`);
                dispatch({ type: ACTIONS.SET_SELECTED_ACQUITTAL, payload: data });
                return data;
            } catch (error) {
                showErrorMessage(error);
                throw error;
            } finally {
                setLoading(false);
            }
        },
        [setLoading, showErrorMessage, dispatch]
    );

    const getAcquittals = useCallback(
        async ({
            type = 'SELF',
            preparedByEmail,
            userEmail,
            status,
            branchCode,
            departmentCode,
            travelRequestId,
            acquittalId,
            createdAtFrom,
            createdAtTo,
            page = 0,
            size = 20
        } = {}) => {
            try {
                setLoading(true);
                const queryParams = new URLSearchParams();

                // Add non-null parameters to query string
                if (type) queryParams.append('type', type);
                if (preparedByEmail) queryParams.append('preparedByEmail', preparedByEmail);
                if (userEmail) queryParams.append('userEmail', userEmail);
                if (status) queryParams.append('status', status);
                if (branchCode) queryParams.append('branchCode', branchCode);
                if (departmentCode) queryParams.append('departmentCode', departmentCode);
                if (travelRequestId) queryParams.append('travelRequestId', travelRequestId.toString());
                if (acquittalId) queryParams.append('acquittalId', acquittalId.toString());
                if (createdAtFrom) queryParams.append('createdAtFrom', createdAtFrom);
                if (createdAtTo) queryParams.append('createdAtTo', createdAtTo);
                if (page !== undefined) queryParams.append('page', page.toString());
                if (size !== undefined) queryParams.append('size', size.toString());

                const data = await apiCaller.get(`${API_ENDPOINTS.TRAVEL_ACQUITALS}?${queryParams.toString()}`);
                dispatch({ type: ACTIONS.SET_ACQUITTALS, payload: data });
                return data;
            } catch (error) {
                showErrorMessage(error);
                throw error;
            } finally {
                setLoading(false);
            }
        },
        [setLoading, showErrorMessage, dispatch]
    );

    const nudgePendingApprover = useCallback(
        async (acquittalId) => {
            try {
                return await apiCaller.post(`${API_ENDPOINTS.TRAVEL_ACQUITALS}/${acquittalId}/nudge`);
            } catch (error) {
                showErrorMessage(error);
                throw error;
            }
        },
        [showErrorMessage]
    );

    return useMemo(
        () => ({
            // State
            acquittals: state.acquittals,
            selectedAcquittal: state.selectedAcquittal,
            loading: state.loading,
            error: state.error,
            pendingAcquittals: state.pendingAcquittals,
            timeline: state.timeline,

            // Operations
            fetchPendingAcquittals,
            approveAcquittals,
            getAcquittalStatuses,
            getTimelineStatuses,
            getAcquittalById,
            getAcquittals,
            nudgePendingApprover
        }),
        [
            state, 
            fetchPendingAcquittals, 
            approveAcquittals, 
            getAcquittalStatuses, 
            getTimelineStatuses, 
            getAcquittalById,
            getAcquittals,
            nudgePendingApprover
        ]
    );
};

export default useAcquittals;
